'use client';

import { QueryKeys } from '@/constants/query-keys';
import { AnalyticsEvent } from '@/enums/analytics-type';
import { favoriteTheatersApiService } from '@/services/(frontend)/profile/favorite-theaters-api.service';
import { UpdateFavoriteTheater } from '@/types/favorite-theater';
import { updatePagedList } from '@/utils/query-update-utils';
import { PagedListOfProfileTheater, ProfileTheater } from '@cinemark/profile-api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDynamicStaleTime } from '../shared/dynamic-stale-time';
import { useIsAuthReady } from './is-auth-ready';

export type UpdateFavoriteTheaterWithAnalyticsType = {
  analyticsEvent?: AnalyticsEvent;
  isFavorite: boolean;
  previousData?: PagedListOfProfileTheater;
  queryKey: readonly string[];
  theaterId: number;
  theaterName: string;
};

type AddFavoriteTheaterType = {
  analyticsEvent?: AnalyticsEvent;
  theaterName: string;
  updateFavoriteTheater: UpdateFavoriteTheater;
};

type RemoveFavoriteTheaterType = {
  analyticsEvent?: AnalyticsEvent;
  theaterName: string;
  updateFavoriteTheater: UpdateFavoriteTheater;
};

export const useFavoriteTheaters = (): ReturnType<typeof useQuery<PagedListOfProfileTheater>> => {
  const updateQueryStaleTime = useDynamicStaleTime();
  const { isAuthenticated } = useIsAuthReady();

  return useQuery<PagedListOfProfileTheater>({
    enabled: isAuthenticated,
    queryFn: async () => {
      const response = await favoriteTheatersApiService.getFavoriteTheaters({ page: 1, pageSize: 100 });

      if (response.headers) {
        updateQueryStaleTime(QueryKeys.user.favoriteTheaters, response.headers);
      }

      return response.data;
    },
    queryKey: QueryKeys.user.favoriteTheaters,
    refetchOnWindowFocus: false,
  });
};

export const useAddFavoriteTheater = ():
ReturnType<typeof useMutation<ProfileTheater, unknown, AddFavoriteTheaterType, UpdateFavoriteTheaterWithAnalyticsType>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ updateFavoriteTheater }) => await favoriteTheatersApiService.addFavoriteTheater(updateFavoriteTheater),
    onError: (_err, _newTheater, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(QueryKeys.user.favoriteTheaters, context.previousData);
      }
    },
    onMutate: async ({ analyticsEvent, theaterName, updateFavoriteTheater }) => {
      await queryClient.cancelQueries({ queryKey: QueryKeys.user.favoriteTheaters });
      const previousData = queryClient.getQueryData<PagedListOfProfileTheater>(QueryKeys.user.favoriteTheaters);

      queryClient.setQueryData<PagedListOfProfileTheater>(QueryKeys.user.favoriteTheaters, oldData =>
        updatePagedList(oldData, theater => theater.theaterId === updateFavoriteTheater.theaterId, theater => ({ ...theater, favorite: true }))
      );
      return {
        analyticsEvent,
        isFavorite: true,
        previousData,
        queryKey: QueryKeys.user.favoriteTheaters,
        theaterId: updateFavoriteTheater.theaterId,
        theaterName,
      };
    },
  });
};

export const useRemoveFavoriteTheater = ():
ReturnType<typeof useMutation<void, unknown, RemoveFavoriteTheaterType, UpdateFavoriteTheaterWithAnalyticsType>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ updateFavoriteTheater }) => await favoriteTheatersApiService.removeFavoriteTheater(updateFavoriteTheater),
    onError: (_err, _newTheater, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(QueryKeys.user.favoriteTheaters, context.previousData);
      }
    },
    onMutate: async ({ analyticsEvent, theaterName, updateFavoriteTheater }) => {
      await queryClient.cancelQueries({ queryKey: QueryKeys.user.favoriteTheaters });
      const previousData = queryClient.getQueryData<PagedListOfProfileTheater>(QueryKeys.user.favoriteTheaters);

      queryClient.setQueryData<PagedListOfProfileTheater>(QueryKeys.user.favoriteTheaters, oldData =>
        updatePagedList(oldData, theater => theater.theaterId === updateFavoriteTheater.theaterId, theater => ({ ...theater, favorite: false }))
      );
      return {
        analyticsEvent,
        isFavorite: false,
        previousData,
        queryKey: QueryKeys.user.favoriteTheaters,
        theaterId: updateFavoriteTheater.theaterId,
        theaterName,
      };
    },
  });
};
